/**
 * Pure validation pipeline for raw FoE export text.
 *
 * Each failure produces an inline-friendly message; we never throw. Order
 * matters — fail-fast — because each step assumes its predecessors passed.
 * See docs/phases/phase-1-import-view.md "Validation pipeline" for the spec.
 */

export interface ValidImport {
  ok: true
  json: {
    CityMapData: Record<string, unknown>
    UnlockedAreas: unknown[]
    CityEntities: Record<string, unknown>
  }
  /** The full parsed top-level object — useful for grabbing optional fields
   *  like `PlayerName` that the validator doesn't enforce. */
  raw: Record<string, unknown>
}

export interface InvalidImport {
  ok: false
  message: string
  /** Stable code for tests / future telemetry. */
  code:
    | 'empty'
    | 'parse'
    | 'not-object'
    | 'missing-keys'
    | 'empty-citymap'
    | 'empty-unlocked'
    | 'empty-entities'
}

export type ImportValidation = ValidImport | InvalidImport

const REQUIRED_KEYS = ['CityMapData', 'UnlockedAreas', 'CityEntities'] as const

export function validateImportText(raw: string): ImportValidation {
  const trimmed = raw.trim()
  if (!trimmed) {
    return {
      ok: false,
      code: 'empty',
      message: 'Paste your city export or drop a file before importing.',
    }
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    return {
      ok: false,
      code: 'parse',
      message: "Couldn't read that as JSON. Check the start and end of what you pasted.",
    }
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {
      ok: false,
      code: 'not-object',
      message:
        "This doesn't look like a Forge of Empires city export — it should be a JSON object with CityMapData, UnlockedAreas and CityEntities.",
    }
  }

  const obj = parsed as Record<string, unknown>
  const missing = REQUIRED_KEYS.filter((k) => !(k in obj))
  if (missing.length > 0) {
    const name = missing[0]
    return {
      ok: false,
      code: 'missing-keys',
      message: `This doesn't look like a Forge of Empires city export — \`${name}\` is missing. Make sure you used "Copy citymap data" in FoE Helper.`,
    }
  }

  const cityMapData = obj.CityMapData
  const unlockedAreas = obj.UnlockedAreas
  const cityEntities = obj.CityEntities

  if (
    !cityMapData ||
    typeof cityMapData !== 'object' ||
    Array.isArray(cityMapData) ||
    Object.keys(cityMapData as Record<string, unknown>).length === 0
  ) {
    return {
      ok: false,
      code: 'empty-citymap',
      message: 'Your city map is empty. Did the export complete?',
    }
  }

  if (!Array.isArray(unlockedAreas) || unlockedAreas.length === 0) {
    return {
      ok: false,
      code: 'empty-unlocked',
      message:
        'No unlocked areas in this export. The file may be partial — try copying again from FoE Helper.',
    }
  }

  if (
    !cityEntities ||
    typeof cityEntities !== 'object' ||
    Array.isArray(cityEntities) ||
    Object.keys(cityEntities as Record<string, unknown>).length === 0
  ) {
    return {
      ok: false,
      code: 'empty-entities',
      message:
        'The catalogue (CityEntities) in this export is empty. The file may be from an outdated version of FoE Helper.',
    }
  }

  return {
    ok: true,
    json: {
      CityMapData: cityMapData as Record<string, unknown>,
      UnlockedAreas: unlockedAreas,
      CityEntities: cityEntities as Record<string, unknown>,
    },
    raw: obj,
  }
}
